import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const getToken = () => localStorage.getItem("prism_token");

/** Build an absolute URL for a backend-relative path (like an attachment or
 * avatar URL). Automatically appends the auth token as a query param so that
 * <img>/<video> tags can load protected files without JS.
 *
 * External URLs (http/https) are returned untouched — we never leak the token
 * to third-party origins. */
export const authedUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const abs = `${BACKEND_URL}${path}`;
  const tok = getToken();
  if (!tok) return abs;
  return `${abs}${abs.includes("?") ? "&" : "?"}token=${encodeURIComponent(tok)}`;
};

export const api = axios.create({
  baseURL: API_BASE,
});

/**
 * Normalise any axios error (or raw value) into a human-readable string.
 * FastAPI returns `detail` as either:
 *   • string                                  → HTTPException(detail="...")
 *   • array of {type, loc, msg, input, ctx}   → 422 Pydantic validation
 *   • object                                  → custom shape (rare)
 * Rendering any of the last two directly as a React child crashes the UI,
 * so every caller should run error messages through this helper first.
 */
export function apiErrorMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        const msg = d?.msg || d?.message || "invalid value";
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return String(detail);
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("prism_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Top-bar branch view (admins only; the server ignores it for everyone else).
  const branchView = localStorage.getItem("prism_view_branch");
  if (branchView) config.headers["X-Branch-Id"] = branchView;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("prism_token");
      localStorage.removeItem("prism_user");
      localStorage.removeItem("prism_view_branch");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    // Normalise FastAPI's error payload BEFORE any UI code reads it.
    // Pydantic 422 responses have `detail` as an array of error objects,
    // which crashes React when rendered as a child. Keep the raw payload
    // on `_rawData` for debugging, but overwrite `detail` with a string.
    if (err?.response && typeof err.response.data === "object" && err.response.data !== null) {
      try {
        const safeMsg = apiErrorMessage(err, "Request failed");
        err.response._rawData = err.response.data;
        err.response.data = { ...err.response.data, detail: safeMsg };
      } catch { /* defensive — never let the interceptor itself throw */ }
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (data) => api.post("/auth/register", data),
  me: () => api.get("/auth/me"),
};

export const dataApi = {
  dashboard: () => api.get("/dashboard"),
  aiInsights: () => api.post("/ai/insights"),

  employees: (params) => api.get("/employees", { params }),
  employee: (id) => api.get(`/employees/${id}`),
  employeeAnalytics: (id) => api.get(`/employees/${id}/analytics`),
  createEmployee: (data) => api.post("/employees", data),

  teams: () => api.get("/teams"),
  team: (id) => api.get(`/teams/${id}`),

  projects: () => api.get("/projects"),
  project: (id) => api.get(`/projects/${id}`),

  tasks: (params) => api.get("/tasks", { params }),
  task: (id) => api.get(`/tasks/${id}`),
  createTask: (data) => api.post("/tasks", data),
  updateTask: (id, data) => api.patch(`/tasks/${id}`, data),
  uploadTaskAttachment: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/tasks/${id}/attachments`, fd);
  },
  deleteTaskAttachment: (id, attId) => api.delete(`/tasks/${id}/attachments/${attId}`),

  attendance: (params) => api.get("/attendance", { params }),
  attendanceSummary: (days = 30) => api.get(`/attendance/summary?days=${days}`),

  workload: () => api.get("/workload"),
  risks: () => api.get("/risks"),
  leaderboard: (metric) => api.get("/leaderboard", { params: { metric } }),
  reportOverview: () => api.get("/reports/overview"),

  chat: (message, session_id) => api.post("/ai/chat", { message, session_id }),
  chatSessions: () => api.get("/ai/sessions"),
  chatMessages: (sid) => api.get(`/ai/sessions/${sid}/messages`),
};
